import { useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Anchor, Loader, SimpleGrid, Tabs, Text } from '@mantine/core';
import { type DatesRangeValue } from '@mantine/dates';
import { FinanceDateRangeInput } from '@repo/date-range-picker';
import {
  IoCalendarOutline,
  IoCashOutline,
  IoDownloadOutline,
  IoRemoveCircleOutline,
  IoTrendingUpOutline,
  IoWalletOutline,
} from 'react-icons/io5';
import { Breadcrumbs, OptionsTable, type OptionsTableColumn } from '@components';
import { sellerHelpUrl } from '@shared/utils/seller-help-url';
import { storageFileUrl } from '@shared/utils/storage-file-url';
import { sellerFinanceApi } from '@modules/seller-finance/api/seller-finance.api';
import type {
  SellerLedgerEntryRow,
  SellerUpdDocumentRow,
} from '@modules/seller-finance/types';
import { SELLER_ME_QUERY_KEY, sellerOnboardingApi } from '@modules/seller-onboarding/api';
import { Alert, Button, PillGroup, RefetchButton } from '@shared/ui';
import { formatMoneyRub } from '@shared/utils/formatUtils';
import '@shared/styles/page.css';
import '../SellerMainPage/SellerMainPage.css';
import './SellerFinancePage.css';

function utcDateLabel(isoDate: string): string {
  const [y, m, d] = isoDate.split('-').map((x) => parseInt(x, 10));
  const dt = new Date(Date.UTC(y, m - 1, d));
  return dt.toLocaleDateString('ru-RU', {
    timeZone: 'UTC',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

function localCalendarYmd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function roundMoney2(n: number): number {
  return Math.round(n * 100) / 100;
}

type PeriodPreset = 'today' | 'yesterday' | 'week' | 'month' | 'custom';

function localRangeForPreset(
  preset: Exclude<PeriodPreset, 'custom'>,
): { dateFrom: string; dateTo: string } {
  const now = new Date();
  const to = localCalendarYmd(now);
  if (preset === 'today') return { dateFrom: to, dateTo: to };

  const fromDate = new Date(now);
  if (preset === 'yesterday') {
    fromDate.setDate(fromDate.getDate() - 1);
    const day = localCalendarYmd(fromDate);
    return { dateFrom: day, dateTo: day };
  }
  if (preset === 'week') {
    fromDate.setDate(fromDate.getDate() - 6);
    return { dateFrom: localCalendarYmd(fromDate), dateTo: to };
  }
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
  return { dateFrom: localCalendarYmd(monthStart), dateTo: to };
}

const PRESET_LABELS: { id: PeriodPreset; label: string }[] = [
  { id: 'today', label: 'Сегодня' },
  { id: 'yesterday', label: 'Вчера' },
  { id: 'week', label: 'Неделя' },
  { id: 'month', label: 'Месяц' },
  { id: 'custom', label: 'Свой период' },
];

function formatPeriod(row: SellerUpdDocumentRow): string {
  if (!row.accrualPeriodStart || !row.accrualPeriodEndExclusive) return '—';
  const start = new Date(row.accrualPeriodStart);
  const endEx = new Date(row.accrualPeriodEndExclusive);
  const endDay = new Date(endEx.getTime() - 86400000);
  const fmt = (d: Date) =>
    d.toLocaleDateString('ru-RU', {
      timeZone: 'UTC',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  return `${fmt(start)} — ${fmt(endDay)}`;
}

function isPickupOnReceiptStornoMeta(meta: unknown): boolean {
  return (
    !!meta &&
    typeof meta === 'object' &&
    (meta as { pickupOnReceipt?: unknown }).pickupOnReceipt === true
  );
}

function ledgerKindLabel(kind: string, meta?: unknown): string {
  switch (kind) {
    case 'ORDER_ITEMS_REVENUE':
      return 'Выручка по заказу';
    case 'PICKUP_ON_RECEIPT_REVENUE':
      return 'Самовывоз (оплата при получении)';
    case 'PLATFORM_COMMISSION':
      return 'Комиссия платформы';
    case 'PROMOTION_DEDUCTION':
      return 'Промо и скидки';
    case 'DELIVERY_ADJUSTMENT':
      return 'Доставка';
    case 'ADVERTISING_DEDUCTION':
      return 'Продвижение / реклама';
    case 'SELLER_ORDER_CANCELLATION_PENALTY':
      return 'Штраф за отмену / возврат';
    case 'WEEKLY_PAYOUT_TRANSFER':
      return 'Выплата на счёт';
    case 'ORDER_ACCRUAL_STORNO':
      return isPickupOnReceiptStornoMeta(meta)
        ? 'Возврат (оплата при получении)'
        : 'Возврат';
    case 'MANUAL_ADJUSTMENT':
      return 'Корректировка баланса';
    case 'ADVERTISING_WALLET_TRANSFER':
      return 'Перевод на рекламный баланс';
    default:
      return kind;
  }
}

function ledgerOrderNumber(row: SellerLedgerEntryRow): string | null {
  const meta = row.meta;
  if (meta && typeof meta === 'object') {
    const n = (meta as { orderNumber?: unknown }).orderNumber;
    if (typeof n === 'string' && n.trim()) return n.trim();
  }
  return null;
}

function sendStatusLabel(status: string): string {
  switch (status) {
    case 'PENDING':
      return 'Ожидает';
    case 'MOCK_SENT':
    case 'ISSUED':
      return 'Сформирован';
    case 'FAILED':
      return 'Ошибка формирования';
    default:
      return status;
  }
}

function MetricCard(props: {
  title: string;
  hint: string;
  value: string;
  valueColor?: string;
  loading?: boolean;
  tone?: 'accent' | 'blue' | 'orange' | 'green';
  icon?: ReactNode;
}) {
  const tone = props.tone ?? 'accent';
  return (
    <div className={`seller-dashboard-metric seller-dashboard-metric--${tone}`}>
      <div className="seller-dashboard-metric__top">
        <Text className="seller-dashboard-metric__title">{props.title}</Text>
        <span className="seller-dashboard-metric__dot" aria-hidden>
          {props.icon}
        </span>
      </div>
      <Text className="seller-dashboard-metric__value" c={props.valueColor}>
        {props.loading ? '…' : props.value}
      </Text>
      <Text className="seller-dashboard-metric__hint">{props.hint}</Text>
    </div>
  );
}

export default function SellerFinancePage() {
  const queryClient = useQueryClient();
  const [preset, setPreset] = useState<PeriodPreset>('week');
  const [customRange, setCustomRange] = useState<DatesRangeValue>([null, null]);
  const { data: app, isLoading: appLoading } = useQuery({
    queryKey: SELLER_ME_QUERY_KEY,
    queryFn: () => sellerOnboardingApi.getMe(),
  });

  const docsQuery = useQuery({
    queryKey: ['seller-finance', 'upd-documents'],
    queryFn: () => sellerFinanceApi.getMyUpdDocuments(),
    enabled: app?.status === 'APPROVED',
  });

  const ledgerQuery = useQuery({
    queryKey: ['seller-finance', 'ledger'],
    queryFn: () => sellerFinanceApi.getMyLedger(),
    enabled: app?.status === 'APPROVED',
  });

  const { dateFrom, dateTo } = useMemo(() => {
    if (preset === 'custom') {
      const [a, b] = customRange;
      if (!a || !b) return localRangeForPreset('week');
      const from = typeof a === 'string' ? a : localCalendarYmd(a);
      const to = typeof b === 'string' ? b : localCalendarYmd(b);
      return from <= to ? { dateFrom: from, dateTo: to } : { dateFrom: to, dateTo: from };
    }
    return localRangeForPreset(preset);
  }, [preset, customRange]);

  const ledgerRows = ledgerQuery.data?.entries ?? [];
  const filteredLedgerRows = useMemo(
    () =>
      ledgerRows.filter((row) => {
        const d = localCalendarYmd(new Date(row.occurredAt));
        return d >= dateFrom && d <= dateTo;
      }),
    [ledgerRows, dateFrom, dateTo],
  );

  const periodTotals = useMemo(() => {
    const isPickupOnReceiptStorno = (row: SellerLedgerEntryRow) =>
      row.kind === 'ORDER_ACCRUAL_STORNO' &&
      !!row.meta &&
      typeof row.meta === 'object' &&
      (row.meta as { pickupOnReceipt?: unknown }).pickupOnReceipt === true;

    const byKind = filteredLedgerRows.reduce<Record<string, number>>((acc, row) => {
      acc[row.kind] = (acc[row.kind] ?? 0) + row.amountRub;
      return acc;
    }, {});

    const deliveryRows = filteredLedgerRows.filter((row) => row.kind === 'DELIVERY_ADJUSTMENT');
    const deliveryCompensationToRevenue = deliveryRows.reduce(
      (acc, row) => acc + (row.amountRub > 0 ? row.amountRub : 0),
      0,
    );
    const deliveryDeduction = deliveryRows.reduce(
      (acc, row) => acc + (row.amountRub < 0 ? -row.amountRub : 0),
      0,
    );

    const pickupStorno = filteredLedgerRows
      .filter(isPickupOnReceiptStorno)
      .reduce((acc, row) => acc + row.amountRub, 0);
    const onlineStorno = filteredLedgerRows
      .filter((row) => row.kind === 'ORDER_ACCRUAL_STORNO' && !isPickupOnReceiptStorno(row))
      .reduce((acc, row) => acc + row.amountRub, 0);

    const onlineRevenue = roundMoney2(
      (byKind.ORDER_ITEMS_REVENUE ?? 0) + deliveryCompensationToRevenue + onlineStorno,
    );
    const onReceiptRevenue = roundMoney2(
      (byKind.PICKUP_ON_RECEIPT_REVENUE ?? 0) + pickupStorno,
    );
    const grossRevenue = roundMoney2(onlineRevenue + onReceiptRevenue);
    const commission = Math.max(0, -(byKind.PLATFORM_COMMISSION ?? 0));
    const promotion = Math.max(
      0,
      -((byKind.PROMOTION_DEDUCTION ?? 0) + (byKind.ADVERTISING_DEDUCTION ?? 0)),
    );
    const penalties = Math.max(0, -(byKind.SELLER_ORDER_CANCELLATION_PENALTY ?? 0));
    const platformDeduction = commission + promotion + penalties + deliveryDeduction;
    const periodNet = roundMoney2(grossRevenue - platformDeduction);

    return {
      grossRevenue,
      onlineRevenue,
      onReceiptRevenue,
      commission,
      deliveryDeduction,
      promotion,
      penalties,
      platformDeduction,
      periodNet,
    };
  }, [filteredLedgerRows]);

  const ledgerColumns: OptionsTableColumn<SellerLedgerEntryRow>[] = useMemo(
    () => [
      {
        key: 'occurredAt',
        label: 'Дата',
        render: (row) => new Date(row.occurredAt).toLocaleString('ru-RU'),
      },
      {
        key: 'operation',
        label: 'Операция',
        render: (row) => {
          const label = ledgerKindLabel(row.kind, row.meta);
          const title = row.title?.trim();
          return <span className="seller-finance-op-label">{title || label}</span>;
        },
      },
      {
        key: 'order',
        label: 'Заказ',
        render: (row) => {
          const orderNumber = ledgerOrderNumber(row);
          if (row.orderId == null && !orderNumber) {
            return <span className="seller-finance-muted">—</span>;
          }
          const label = orderNumber ?? `№${row.orderId}`;
          if (row.orderId == null) {
            return label;
          }
          return (
            <Anchor component={Link} to={`/orders/${row.orderId}`} size="sm">
              {label}
            </Anchor>
          );
        },
      },
      {
        key: 'amountRub',
        label: 'Сумма',
        render: (row) => (
          <span
            className={
              row.amountRub >= 0
                ? 'seller-finance-amount seller-finance-amount--plus'
                : 'seller-finance-amount seller-finance-amount--minus'
            }
          >
            {row.amountRub >= 0 ? '+' : ''}
            {formatMoneyRub(row.amountRub)}
          </span>
        ),
      },
    ],
    [],
  );

  const refreshFinance = () =>
    queryClient.invalidateQueries({ queryKey: ['seller-finance'] });

  const handleDownloadPdf = async (
    row: SellerUpdDocumentRow,
    kind: 'act' | 'invoice',
  ) => {
    const publicUrl =
      kind === 'invoice' ? row.invoicePdfPublicUrl : row.pdfPublicUrl;
    if (publicUrl) {
      window.open(storageFileUrl(publicUrl) || publicUrl, '_blank', 'noopener,noreferrer');
      return;
    }
    try {
      const blob = await sellerFinanceApi.downloadPdfBlob(row.id, kind);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${kind === 'invoice' ? 'schet-faktura' : 'akt'}-${row.documentNumber}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      // см. Network / консоль
    }
  };

  const financeColumns: OptionsTableColumn<SellerUpdDocumentRow>[] = useMemo(
    () => [
      {
        key: 'documentNumber',
        label: 'Документ',
        render: (row) => (
          <div>
            <span className="seller-finance-doc-number">{row.documentNumber}</span>
            {row.paidAt ? (
              <span className="seller-finance-doc-meta">
                выплата:{' '}
                {new Date(row.paidAt).toLocaleString('ru-RU', { timeZone: 'UTC' })}
              </span>
            ) : null}
          </div>
        ),
      },
      {
        key: 'period',
        label: 'Период начислений',
        render: (row) => formatPeriod(row),
      },
      {
        key: 'commissionAmount',
        label: 'Удержание платформы',
        render: (row) => formatMoneyRub(row.commissionAmount),
      },
      {
        key: 'netAmount',
        label: 'Сумма к выплате',
        render: (row) => formatMoneyRub(row.netAmount),
      },
      {
        key: 'sendStatus',
        label: 'Статус',
        render: (row) => sendStatusLabel(row.sendStatus),
      },
      {
        key: 'invoice',
        label: 'Счёт-фактура',
        style: { textAlign: 'center', width: 120 },
        render: (row) => (
          <div className="seller-finance-doc-dl">
            <Button
              type="button"
              variant="secondary-icon"
              className="seller-finance-doc-dl__btn"
              disabled={!row.hasInvoicePdf}
              aria-label="Скачать счёт-фактуру"
              onClick={() => {
                void handleDownloadPdf(row, 'invoice');
              }}
            >
              <IoDownloadOutline size={16} />
            </Button>
          </div>
        ),
      },
      {
        key: 'act',
        label: 'Акт',
        style: { textAlign: 'center', width: 72 },
        render: (row) => (
          <div className="seller-finance-doc-dl">
            <Button
              type="button"
              variant="secondary-icon"
              className="seller-finance-doc-dl__btn"
              disabled={!row.pdfPublicUrl && !row.hasLocalPdf}
              aria-label="Скачать акт"
              onClick={() => {
                void handleDownloadPdf(row, 'act');
              }}
            >
              <IoDownloadOutline size={16} />
            </Button>
          </div>
        ),
      },
    ],
    [],
  );

  if (appLoading || !app) {
    return (
      <div className="page-container seller-finance-page seller-finance-page--loading">
        <Loader />
      </div>
    );
  }

  if (app.status !== 'APPROVED') {
    return (
      <div className="page-container seller-finance-page">
        <Breadcrumbs items={[{ label: 'Главная', path: '/' }, { label: 'Финансы' }]} />
        <div className="seller-finance-head">
          <h1 className="seller-finance-title">Финансы</h1>
          <p className="seller-finance-subtitle">Раздел будет доступен после одобрения магазина</p>
        </div>
        <Alert tone="info">
          Раздел будет доступен после одобрения магазина модератором.
        </Alert>
      </div>
    );
  }

  const rows = docsQuery.data ?? [];
  const currentBalanceRub = ledgerQuery.data?.currentBalanceRub ?? 0;
  const week = ledgerQuery.data?.openWeekAccruals;
  const planned = ledgerQuery.data?.plannedPayout;
  const ledgerLoading = ledgerQuery.isPending || ledgerQuery.isFetching;

  const payoutScheduleText = planned
    ? new Date(planned.scheduledAt).toLocaleString('ru-RU', {
      timeZone: 'UTC',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    })
    : '—';

  const plannedHint = planned
    ? `за ${utcDateLabel(planned.salesDateFrom)} — ${utcDateLabel(planned.salesDateTo)}${planned.alreadyPaid ? ' · уже проведена' : ''
    }`
    : week
      ? 'ближайший прогон по расписанию'
      : 'дата ближайшей выплаты';

  return (
    <div className="page-container seller-finance-page">
      <Breadcrumbs items={[{ label: 'Главная', path: '/' }, { label: 'Финансы' }]} />

      <Tabs defaultValue="operations" keepMounted={false} className="seller-finance-tabs">
        <div className="seller-finance-sticky">
          <div className="seller-finance-head">
            <h1 className="seller-finance-title">Финансы</h1>
            <p className="seller-finance-subtitle">
              Баланс, операции и закрывающие документы.{' '}
              <a
                href={sellerHelpUrl('pen-where')}
                className="seller-finance-help-link"
                target="_blank"
                rel="noopener noreferrer"
              >
                Штрафы и удержания в «Помощи»
              </a>
            </p>
          </div>

          <Tabs.List className="seller-finance-tabs-list">
            <Tabs.Tab value="operations" className="seller-finance-tabs-tab">
              Операции
            </Tabs.Tab>
            <Tabs.Tab value="documents" className="seller-finance-tabs-tab">
              Документы
            </Tabs.Tab>
          </Tabs.List>
        </div>

        <div className="seller-finance-scroll">
          {app.accountType === 'SELF_EMPLOYED' &&
            app.tbankSeRecipientStatus !== 'ACTIVE' ? (
            <Alert
              tone={app.tbankSeRecipientStatus === 'ERROR' ? 'error' : 'warning'}
              mb="md"
            >
              {app.tbankSeRecipientStatus === 'ERROR'
                ? `Не получилось добавить вас в выплаты Т‑Банка: ${app.tbankSeAddError ?? 'ошибка'}. Напишите в поддержку.`
                : 'Чтобы получать выплаты, подтвердите запрос от площадки в приложении или на сайте «Мой налог». После этого чеки по выплатам будут приходить автоматически.'}
            </Alert>
          ) : null}

          <Tabs.Panel value="operations" className="seller-finance-tabs-panel">
            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
              <MetricCard
                title="К выплате"
                hint="Невыплаченный остаток (без самовывоза при получении)"
                value={formatMoneyRub(currentBalanceRub)}
                loading={ledgerLoading}
                tone="accent"
                icon={<IoWalletOutline />}
              />
              <MetricCard
                title="Плановая выплата"
                hint={`${payoutScheduleText} · ${plannedHint}`}
                value={formatMoneyRub(planned?.netAmountRub ?? 0)}
                loading={ledgerLoading}
                tone="green"
                icon={<IoCalendarOutline />}
              />
            </SimpleGrid>

            <div className="seller-dashboard__period seller-finance-period">
              <Text className="seller-dashboard__period-label">Период</Text>
              <PillGroup
                ariaLabel="Период"
                options={PRESET_LABELS.map((p) => ({ value: p.id, label: p.label }))}
                value={preset}
                onChange={setPreset}
              />
              {preset === 'custom' ? (
                <div className="seller-dashboard__period-dates">
                  <FinanceDateRangeInput
                    value={customRange}
                    onChange={setCustomRange}
                    valueFormat="DD.MM.YYYY"
                    placeholder="Выберите даты"
                    size="sm"
                    radius="md"
                  />
                </div>
              ) : null}
            </div>

            <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="md">
              <MetricCard
                title="Выручка"
                hint="за выбранный период"
                value={formatMoneyRub(periodTotals.grossRevenue)}
                loading={ledgerLoading}
                tone="accent"
                icon={<IoCashOutline />}
              />
              <MetricCard
                title="Удержание"
                hint={[
                  periodTotals.commission > 0
                    ? `комиссия и эквайринг ${formatMoneyRub(periodTotals.commission)}`
                    : null,
                  periodTotals.deliveryDeduction > 0
                    ? `доставка ${formatMoneyRub(periodTotals.deliveryDeduction)}`
                    : null,
                  periodTotals.promotion > 0
                    ? `продвижение ${formatMoneyRub(periodTotals.promotion)}`
                    : null,
                  periodTotals.penalties > 0
                    ? `штрафы ${formatMoneyRub(periodTotals.penalties)}`
                    : null,
                ]
                  .filter(Boolean)
                  .join(' · ') || 'комиссия, доставка, продвижение, штрафы'}
                value={`−${formatMoneyRub(periodTotals.platformDeduction)}`}
                loading={ledgerLoading}
                tone="orange"
                icon={<IoRemoveCircleOutline />}
              />
              <MetricCard
                title="Итого за период"
                hint="выручка минус удержания"
                value={formatMoneyRub(periodTotals.periodNet)}
                loading={ledgerLoading}
                tone="blue"
                icon={<IoTrendingUpOutline />}
              />
            </SimpleGrid>

            <section className="seller-dashboard-card seller-finance-card">
              <div className="seller-dashboard-card__head">
                <h2 className="seller-dashboard-card__title">История операций</h2>
              </div>
              <div className="seller-finance-table-wrap seller-finance-desktop-table">
                <OptionsTable<SellerLedgerEntryRow>
                  columns={ledgerColumns}
                  data={filteredLedgerRows}
                  loading={ledgerQuery.isLoading}
                  error={ledgerQuery.error}
                  refetch={() => refreshFinance()}
                  emptyMessage="Нет операций за выбранный период."
                />
              </div>
              <div className="seller-finance-mobile-list seller-finance-mobile-list--ops" aria-label="История операций">
                {ledgerQuery.error ? (
                  <RefetchButton
                    refetch={() => refreshFinance()}
                    isLoading={ledgerQuery.isLoading || ledgerQuery.isFetching}
                    errorMessage={
                      ledgerQuery.error instanceof Error
                        ? ledgerQuery.error.message
                        : 'Не удалось загрузить операции'
                    }
                  />
                ) : ledgerQuery.isLoading ? (
                  <div className="seller-finance-mobile-empty">
                    <Loader size="sm" />
                  </div>
                ) : filteredLedgerRows.length === 0 ? (
                  <div className="seller-finance-mobile-empty" role="status">
                    Нет операций за выбранный период.
                  </div>
                ) : (
                  filteredLedgerRows.map((row) => {
                    const orderNumber = ledgerOrderNumber(row);
                    const opLabel = row.title?.trim() || ledgerKindLabel(row.kind, row.meta);
                    return (
                      <div key={row.id} className="seller-finance-mobile-card">
                        <div className="seller-finance-mobile-card__top">
                          <span className="seller-finance-op-label">{opLabel}</span>
                          <span
                            className={
                              row.amountRub >= 0
                                ? 'seller-finance-amount seller-finance-amount--plus'
                                : 'seller-finance-amount seller-finance-amount--minus'
                            }
                          >
                            {row.amountRub >= 0 ? '+' : ''}
                            {formatMoneyRub(row.amountRub)}
                          </span>
                        </div>
                        <div className="seller-finance-mobile-card__meta">
                          {new Date(row.occurredAt).toLocaleString('ru-RU')}
                        </div>
                        <div className="seller-finance-mobile-card__row">
                          <span className="seller-finance-mobile-card__label">Заказ</span>
                          <span className="seller-finance-mobile-card__value">
                            {row.orderId == null && !orderNumber ? (
                              <span className="seller-finance-muted">—</span>
                            ) : row.orderId == null ? (
                              orderNumber
                            ) : (
                              <Anchor component={Link} to={`/orders/${row.orderId}`} size="sm">
                                {orderNumber ?? `№${row.orderId}`}
                              </Anchor>
                            )}
                          </span>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </section>
          </Tabs.Panel>

          <Tabs.Panel value="documents" className="seller-finance-tabs-panel">
            <section className="seller-dashboard-card seller-finance-card seller-finance-card--docs">
              <div className="seller-dashboard-card__head">
                <h2 className="seller-dashboard-card__title">Акт и счёт-фактура</h2>
              </div>
              <div className="seller-finance-table-wrap seller-finance-desktop-table">
                <OptionsTable<SellerUpdDocumentRow>
                  columns={financeColumns}
                  data={rows}
                  loading={docsQuery.isLoading}
                  error={docsQuery.error}
                  refetch={() => refreshFinance()}
                  emptyMessage="Пока нет документов. Они появятся после проведения выплат по вашему магазину."
                />
              </div>
              <div className="seller-finance-mobile-list" aria-label="Документы">
                {docsQuery.error ? (
                  <RefetchButton
                    refetch={() => refreshFinance()}
                    isLoading={docsQuery.isLoading || docsQuery.isFetching}
                    errorMessage={
                      docsQuery.error instanceof Error
                        ? docsQuery.error.message
                        : 'Не удалось загрузить документы'
                    }
                  />
                ) : docsQuery.isLoading ? (
                  <div className="seller-finance-mobile-empty">
                    <Loader size="sm" />
                  </div>
                ) : rows.length === 0 ? (
                  <div className="seller-finance-mobile-empty" role="status">
                    Пока нет документов. Они появятся после проведения выплат по вашему магазину.
                  </div>
                ) : (
                  rows.map((row) => (
                    <div key={row.id} className="seller-finance-mobile-card">
                      <div className="seller-finance-mobile-card__top">
                        <div>
                          <span className="seller-finance-doc-number">{row.documentNumber}</span>
                          {row.paidAt ? (
                            <span className="seller-finance-doc-meta">
                              выплата:{' '}
                              {new Date(row.paidAt).toLocaleString('ru-RU', { timeZone: 'UTC' })}
                            </span>
                          ) : null}
                        </div>
                        <span className="seller-finance-amount seller-finance-amount--plus">
                          {formatMoneyRub(row.netAmount)}
                        </span>
                      </div>
                      <div className="seller-finance-mobile-card__meta">
                        <span>{formatPeriod(row)}</span>
                        <span className="seller-finance-mobile-card__status">
                          {sendStatusLabel(row.sendStatus)}
                        </span>
                      </div>
                      <div className="seller-finance-mobile-card__row">
                        <span className="seller-finance-mobile-card__label">Удержание</span>
                        <span className="seller-finance-mobile-card__value">
                          {formatMoneyRub(row.commissionAmount)}
                        </span>
                      </div>
                      <div className="seller-finance-mobile-card__downloads">
                        <button
                          type="button"
                          className="seller-finance-mobile-dl"
                          disabled={!row.hasInvoicePdf}
                          onClick={() => {
                            void handleDownloadPdf(row, 'invoice');
                          }}
                        >
                          <IoDownloadOutline size={16} aria-hidden />
                          <span>Счёт-фактура</span>
                        </button>
                        <button
                          type="button"
                          className="seller-finance-mobile-dl"
                          disabled={!row.pdfPublicUrl && !row.hasLocalPdf}
                          onClick={() => {
                            void handleDownloadPdf(row, 'act');
                          }}
                        >
                          <IoDownloadOutline size={16} aria-hidden />
                          <span>Акт</span>
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </section>
          </Tabs.Panel>
        </div>
      </Tabs>
    </div>
  );
}
